import { AxiosError } from "axios";
import { useQuery, QueryObserverResult, UseQueryOptions } from "react-query";

import {
  FetcherQueryKey,
  computeIsQueryEnabled,
} from "../../../services/query";
import {
  FieldsResponse,
  BasicFieldsDataEnvelope,
} from "../../../models/fields";
import { useMaybeFabricFeatureFlag } from "..";

import { useMaybeUserId } from "../userId";
import { useMaybeCurrentOperationId } from "../operations";

import {
  FieldsFetcherParams,
  fieldsQueryKeyPrefix,
  computeFieldsQueryKey,
  fieldsFetcher,
  isDataEqual,
  computeResultOfUseQuery,
} from "./fields.query.helpers";

export { fieldsQueryKeyPrefix, computeFieldsQueryKey };

// ####################
// ## useFieldsQuery ##
// ####################

export type ParamsOfUseFieldsQuery = {
  watch?: UseQueryOptions["notifyOnChangeProps"];
  forceDisable?: boolean;
};
export type ResultOfUseFieldsQuery = QueryObserverResult<
  BasicFieldsDataEnvelope,
  AxiosError
>;
export type UseFieldsQuery = (
  params?: ParamsOfUseFieldsQuery,
) => ResultOfUseFieldsQuery;

/**
 * The returned value of this hook will contain the React Query result
 * of the fetching of the fields data.
 *
 * This hook is only meant for use by the hooks in `./basicFields.ts`.
 * Do not use this hook outside these hooks as this could couple us to React Query.
 */
export const useFieldsQuery: UseFieldsQuery = ({
  watch,
  forceDisable,
} = {}) => {
  const userId = useMaybeUserId();
  const operationId = useMaybeCurrentOperationId();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "fields-api",
  });

  const params: FieldsFetcherParams = { userId, operationId, catalogFlag };
  const resultOfUseQuery = useQuery<
    FieldsResponse,
    AxiosError,
    FieldsResponse,
    FetcherQueryKey<FieldsFetcherParams>
  >(computeFieldsQueryKey(params), fieldsFetcher, {
    enabled: computeIsQueryEnabled(params, { forceDisable }),
    notifyOnChangeProps: watch || "tracked",
    isDataEqual,
  });

  return computeResultOfUseQuery(resultOfUseQuery);
};
